/**
 * Briefing the operator acknowledges when a run starts.
 *
 * Compare, the roster, and the planned length used to sit above Sentinel and
 * get scrolled off the moment the first alert landed. They belong in a modal
 * that has to be read once, then closed.
 */
import React from 'react';
import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import { Mission } from '../../services/missionApi';
import MissionCompare from './MissionCompare';
import { formatMeters, formatSpan } from './format';
import { metalSx } from '../../theme/metalButtons';

export interface MissionStartBriefProps {
  open: boolean;
  mission: Mission;
  units?: 'km' | 'mi';
  onAck: () => void;
  /** Opens setup so the route can be rewritten or the scans run again. */
  onEdit?: () => void;
}

const MissionStartBrief: React.FC<MissionStartBriefProps> = ({
  open, mission, units = 'mi', onAck, onEdit,
}) => {
  const stats = mission.route_stats;
  const people = mission.assignees || [];
  const stops = mission.waypoints || [];

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onAck();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ pt: 2.25, pb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.4, display: 'block', mb: 0.75 }}>
          {mission.current_run?.kind === 'rehearsal' ? 'REHEARSAL' : 'LIVE RUN'} · {mission.name}
        </Typography>

        <MissionCompare missionId={mission.id} units={units} />

        <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.35 }}>
            This movement
          </Typography>
          <Line
            label="Route"
            value={[
              stats?.distance != null ? formatMeters(stats.distance, units) : null,
              stats?.duration != null ? formatSpan(stats.duration) : null,
              stats?.profile || mission.route_profile || null,
            ].filter(Boolean).join(' · ') || 'planned route'}
          />
          <Line
            label="Stops"
            value={stops.length ? String(stops.length) : 'none set'}
          />
          <Line
            label="Participants"
            value={people.length ? String(people.length) : 'none assigned'}
          />
          {people.map((person) => (
            <Typography
              key={person.id}
              variant="caption"
              sx={{ display: 'block', fontSize: '0.63rem', color: 'text.secondary', pl: 0.25 }}
            >
              {person.label || person.entity_id || person.device_id}
              {person.device_kind ? ` · ${person.device_kind}` : ''}
              {person.is_observer === false ? ' · no RF' : ''}
            </Typography>
          ))}
          {mission.objective && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.6, fontSize: '0.63rem', color: 'text.secondary', lineHeight: 1.45 }}
            >
              {mission.objective}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.75 }}>
          {onEdit ? (
            <Button
              size="small"
              onClick={onEdit}
              sx={{ fontWeight: 700, fontSize: '0.7rem', ...metalSx('gun') }}
            >
              Edit route
            </Button>
          ) : <span />}
          <Button
            size="small"
            onClick={onAck}
            sx={{
              fontWeight: 800,
              fontSize: '0.7rem',
              px: 1.5,
              ...metalSx('steel'),
            }}
          >
            Acknowledge
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const Line: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.1 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1, minWidth: 0 }} noWrap>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, flexShrink: 0 }}>
      {value}
    </Typography>
  </Box>
);

export default MissionStartBrief;
